import { ComponentIds, HierarchyUtils, TransformUtils, Vector } from '@inter-ikea-core/core';
import { PartKey, PlannerEcsMultiSystem, ProductType, eulerDegreesToRadians } from '@inter-ikea-storageone/range';
import { ObjectHandle } from '@inter-ikea-betrakta/viewer';
import { color } from 'styled-system';
import IvarShadowBoxComponent from '../components/IvarShadowBoxComponent';
/**
 * Adds shadowboxes under IVAR sections
 */
export class IvarShadowBoxSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'IvarShadowBoxSystem', [
            ComponentIds.IS_DRAGGED_COMPONENT_ID,
            ComponentIds.IN_SCENE_COMPONENT_ID,
            ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID
        ]);
        this.shadowBoxMaterial = this.coreApi.ipexViewer.create_material();
        this.coreApi.ipexViewer.set_base_color(this.shadowBoxMaterial, color(0.2, 0.2, 0.2));
        this.coreApi.ipexViewer.set_roughness(this.shadowBoxMaterial, 0.7);
        this.coreApi.ipexViewer.set_metallic(this.shadowBoxMaterial, 0.0);
        this.coreApi.ipexViewer.set_opacity(this.shadowBoxMaterial, 0.3);
    }
    onAdd(entity, newComponent) {
        if (newComponent.typeId === ComponentIds.IN_SCENE_COMPONENT_ID &&
            this.coreApi.entities.isOneOfProductTypes(entity, [
                ProductType.IVAR_SECTION,
                ProductType.IVAR_CORNER_SECTION
            ])) {
            this.createShadowBox(entity);
        }
        else if (newComponent.typeId === ComponentIds.IS_DRAGGED_COMPONENT_ID &&
            this.coreApi.entities.isOneOfProductTypes(entity, [
                ProductType.IVAR_SECTION,
                ProductType.IVAR_CORNER_SECTION
            ])) {
            this.deleteShadowBox(entity);
        }
        else if (newComponent.typeId === ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID) {
            const sectionsInScene = this.coreApi.entities.getByProductTypes([
                ProductType.IVAR_SECTION,
                ProductType.IVAR_CORNER_SECTION
            ]);
            sectionsInScene.forEach(section => this.deleteShadowBox(section));
        }
    }
    onUpdate() { }
    onRemove(entity, oldComponent) {
        if (oldComponent.typeId === ComponentIds.IN_SCENE_COMPONENT_ID &&
            this.coreApi.entities.isOneOfProductTypes(entity, [
                ProductType.IVAR_SECTION,
                ProductType.IVAR_CORNER_SECTION
            ])) {
            this.deleteShadowBox(entity);
        }
        else if (oldComponent.typeId === ComponentIds.IS_DRAGGED_COMPONENT_ID &&
            this.coreApi.entities.isOneOfProductTypes(entity, [
                ProductType.IVAR_SECTION,
                ProductType.IVAR_CORNER_SECTION
            ])) {
            this.createShadowBox(entity);
        }
        else if (oldComponent.typeId === ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID) {
            const sectionsInScene = this.coreApi.entities.getByProductTypes([
                ProductType.IVAR_SECTION,
                ProductType.IVAR_CORNER_SECTION
            ]);
            sectionsInScene.forEach(section => this.createShadowBox(section));
        }
    }
    createShadowBox(section) {
        const shadowBox = this.coreApi.entities.isProductType(section, ProductType.IVAR_CORNER_SECTION)
            ? this.createCornerSectionCornerPost(section)
            : this.createRegularSectionCornerPost(section);
        shadowBox && section.addComponent(new IvarShadowBoxComponent(shadowBox.id));
    }
    createCornerSectionCornerPost(section) {
        const sectionTransform = TransformUtils.worldTransform(section);
        const sectionRotation = Math.round(sectionTransform.rotation.eulerDegrees.Y);
        const vertices = [];
        // 0--------1
        // |        |
        // |        2
        // |      /
        // 4----3
        // The vertices array holds the position of the corners in the corner section as described above
        // This is then used to create three meshes for the shadow box: 0-2-1, 0-3-2 and 0-4-3
        const cornerPost = HierarchyUtils.children(section).find(child => this.coreApi.entities.isProductType(child, ProductType.IVAR_CORNER_POST));
        const cornerPostPosition = cornerPost && TransformUtils.worldTransform(cornerPost).position;
        const cornerPostSize = cornerPost &&
            cornerPost.getComponent(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)?.size
                .Z;
        const halfCornerPostSize = cornerPostSize && cornerPostSize / 2;
        const rightSideUnit = HierarchyUtils.children(section).find(child => this.coreApi.entities.hasPartKey(child, PartKey.SIDE_UNIT_RIGHT));
        const rightSideUnitPosition = rightSideUnit && TransformUtils.worldTransform(rightSideUnit).position;
        const leftSideUnit = HierarchyUtils.children(section).find(child => this.coreApi.entities.hasPartKey(child, PartKey.SIDE_UNIT_LEFT));
        const leftSideUnitPosition = leftSideUnit && TransformUtils.worldTransform(leftSideUnit).position;
        const sideUnitSize = leftSideUnit &&
            leftSideUnit.getComponent(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)
                ?.size.Z;
        const halfSideUnitSize = sideUnitSize && sideUnitSize / 2;
        if (!cornerPostPosition ||
            !halfCornerPostSize ||
            !leftSideUnitPosition ||
            !rightSideUnitPosition ||
            !halfSideUnitSize) {
            return null;
        }
        if (sectionRotation === 0) {
            // 0
            vertices.push(new Vector((cornerPostPosition.X - halfCornerPostSize) / 1000, 0.005, (cornerPostPosition.Z - halfCornerPostSize) / 1000));
            // 1
            vertices.push(new Vector(rightSideUnitPosition.X / 1000, 0.005, (rightSideUnitPosition.Z - halfSideUnitSize) / 1000));
            // 2
            vertices.push(new Vector(rightSideUnitPosition.X / 1000, 0.005, (rightSideUnitPosition.Z + halfSideUnitSize) / 1000));
            // 3
            vertices.push(new Vector((leftSideUnitPosition.X + halfSideUnitSize) / 1000, 0.005, leftSideUnitPosition.Z / 1000));
            // 4
            vertices.push(new Vector((leftSideUnitPosition.X - halfSideUnitSize) / 1000, 0.005, leftSideUnitPosition.Z / 1000));
        }
        else if (sectionRotation === -90) {
            // 0
            vertices.push(new Vector((cornerPostPosition.X + halfCornerPostSize) / 1000, 0.005, (cornerPostPosition.Z - halfCornerPostSize) / 1000));
            // 1
            vertices.push(new Vector((rightSideUnitPosition.X + halfSideUnitSize) / 1000, 0.005, rightSideUnitPosition.Z / 1000));
            // 2
            vertices.push(new Vector((rightSideUnitPosition.X - halfSideUnitSize) / 1000, 0.005, rightSideUnitPosition.Z / 1000));
            // 3
            vertices.push(new Vector(leftSideUnitPosition.X / 1000, 0.005, (leftSideUnitPosition.Z + halfSideUnitSize) / 1000));
            // 4
            vertices.push(new Vector(leftSideUnitPosition.X / 1000, 0.005, (leftSideUnitPosition.Z - halfSideUnitSize) / 1000));
        }
        else if (sectionRotation === 90) {
            // 0
            vertices.push(new Vector((cornerPostPosition.X - halfCornerPostSize) / 1000, 0.005, (cornerPostPosition.Z + halfCornerPostSize) / 1000));
            // 1
            vertices.push(new Vector((rightSideUnitPosition.X - halfSideUnitSize) / 1000, 0.005, rightSideUnitPosition.Z / 1000));
            // 2
            vertices.push(new Vector((rightSideUnitPosition.X + halfSideUnitSize) / 1000, 0.005, rightSideUnitPosition.Z / 1000));
            // 3
            vertices.push(new Vector(leftSideUnitPosition.X / 1000, 0.005, (leftSideUnitPosition.Z - halfSideUnitSize) / 1000));
            // 4
            vertices.push(new Vector(leftSideUnitPosition.X / 1000, 0.005, (leftSideUnitPosition.Z + halfSideUnitSize) / 1000));
        }
        else if (sectionRotation === 180) {
            // 0
            vertices.push(new Vector((cornerPostPosition.X + halfCornerPostSize) / 1000, 0.005, (cornerPostPosition.Z + halfCornerPostSize) / 1000));
            // 1
            vertices.push(new Vector(rightSideUnitPosition.X / 1000, 0.005, (rightSideUnitPosition.Z + halfSideUnitSize) / 1000));
            // 2
            vertices.push(new Vector(rightSideUnitPosition.X / 1000, 0.005, (rightSideUnitPosition.Z - halfSideUnitSize) / 1000));
            // 3
            vertices.push(new Vector((leftSideUnitPosition.X - halfSideUnitSize) / 1000, 0.005, leftSideUnitPosition.Z / 1000));
            // 4
            vertices.push(new Vector((leftSideUnitPosition.X + halfSideUnitSize) / 1000, 0.005, leftSideUnitPosition.Z / 1000));
        }
        if (vertices.length === 0) {
            return null;
        }
        const shadowBox = new ObjectHandle(this.coreApi.ipexViewer.create_empty_object(), this.coreApi.ipexViewer);
        this.coreApi.ipexViewer.set_material(shadowBox.id, this.shadowBoxMaterial);
        return shadowBox;
    }
    createRegularSectionCornerPost(section) {
        const transform = TransformUtils.worldTransform(section);
        const size = section.getComponent(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)?.size;
        if (!size)
            return;
        const boxPosition = {
            x: transform.position.X / 1000,
            y: 0.005,
            z: transform.position.Z / 1000
        };
        const boxSize = { width: size.X / 1000, height: 0.01, depth: size.Z / 1000 };
        const boxAngle = eulerDegreesToRadians(transform.rotation.toEulerDegrees().Y);
        const shadowBox = new ObjectHandle(this.coreApi.ipexViewer.create_box(boxSize.width, boxSize.height, boxSize.depth), this.coreApi.ipexViewer);
        this.coreApi.ipexViewer.set_material(shadowBox.id, this.shadowBoxMaterial);
        this.coreApi.ipexViewer.set_position(shadowBox.id, boxPosition.x, boxPosition.y, boxPosition.z);
        this.coreApi.ipexViewer.set_rotation(shadowBox.id, 0, boxAngle, 0);
        return shadowBox;
    }
    deleteShadowBox(section) {
        const shadowBoxId = section.getComponent(IvarShadowBoxComponent.id)
            ?.shadowBoxId;
        shadowBoxId && this.coreApi.ipexViewer.delete_object(shadowBoxId);
    }
    toArray(vector) {
        return [vector.X, vector.Y, vector.Z];
    }
}
//# sourceMappingURL=IvarShadowBoxSystem.js.map